import * as React from 'react';
import { GetServerSideProps } from 'next';

import { PageProvider } from '@packages/shared';
import { Page, CmsPage as CmsPageType, CmsModuleType } from '@packages/common';
import { Choice, Cookie } from '@lib/dy';

import { DYContextRenewer } from '../components/DYContextRenewer/DYContextRenewer';

import { findContentfulModules, getContentfulContent, getContentfulRedirect } from '../pageUtils';
import { dyClient, DyProvider } from '../pageUtils/dyClient';
import { Head } from '../components/Layouts/Head';
import { SessionStart } from '../components/Layouts/SessionStart';
import { DyScripts } from '../components/Layouts/DyScripts';
import { SharedWrapper } from '../contexts/SharedContext/SharedWrapper';
import { getSharedProps, SharedProps } from '../pageUtils/getSharedProps';
import { CmsPage } from '../components/contentful';

export interface ContentfulPageProps {
  type: Page.contentful;
  url: string;
  content: CmsPageType;
  dyServerResult?: {
    cookies: Cookie[];
    choices: Choice[];
  };
  sharedProps: SharedProps;
}

export const ContentfulPage: React.FC<ContentfulPageProps> = ({
  content,
  url,
  dyServerResult = { cookies: [], choices: [] },
  sharedProps,
}) => {
  if (!content || Object.keys(content).length === 0) {
    return null;
  }

  const page = url === '/' ? Page.home : Page.contentful;
  const { title, meta } = content;
  const headProps = {
    title,
    description: meta?.metaDescription,
    ogTitle: title,
    ogDescription: meta?.ogDescription,
    ogImageUrl: meta?.ogImage,
    canonicalUrl: meta?.canonical,
    robots: meta?.robots.join(','),
  };

  return (
    <PageProvider page={page}>
      <DyScripts />
      <DyProvider
        cookies={dyServerResult.cookies}
        choices={dyServerResult.choices}
        context={{
          page: {
            type: 'HOMEPAGE',
            data: [url],
          },
        }}
      >
        <SharedWrapper {...sharedProps}>
          <DYContextRenewer page={page} />
          <Head {...headProps} />
          <SessionStart />
          <CmsPage page={content} />
        </SharedWrapper>
      </DyProvider>
    </PageProvider>
  );
};

export const contentfulGetServerSideProps: GetServerSideProps<ContentfulPageProps> = async (ctx) => {
  const { resolvedUrl, req, query } = ctx;
  const sharedProps = await getSharedProps(ctx);
  const { abTestConfig } = sharedProps;
  const [url] = resolvedUrl.split('?');
  const { preview } = query;
  const content = await getContentfulContent(url, { preview }, abTestConfig.trafficSlotNumber);
  const redirect = getContentfulRedirect(content);

  if (redirect) {
    const { redirectUrl, statusCode } = redirect;
    return { redirect: { destination: redirectUrl, statusCode: statusCode as any } };
  }

  if (!content) {
    return { redirect: { destination: '/404', permanent: false } };
  }

  const dyCampaigns = findContentfulModules(content, CmsModuleType.DY_CAMPAIGN).map(
    (module) => module?.['campaignName'],
  );

  const dyServerResult = await dyClient.choose({
    req,
    body: {
      context: {
        page: {
          type: 'HOMEPAGE',
          data: [url],
        },
      },
      selector: {
        names: dyCampaigns,
      },
    },
  });

  return { props: { type: Page.contentful, url, content, dyServerResult, sharedProps } };
};
